import {KeyboardEvent, memo, useCallback} from 'react';

import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {
  AppButton,
  AppButtonVariant,
  ButtonContainer,
} from '@/app/shared-components';
import TermsAndConditionsModal from '@/app/shared-components/TermsAndConditions/TermsAndConditionsModal';

import {
  KYC_SOURCE_OF_FUNDS_OPTION_IDS,
  KycSourceOfFundsOptionId,
} from './constants';
import {useViewModelProvider, ViewModelProvider} from './ViewModelContext';

import './styles';

type KycSourceOfFundsOptionProps = {
  optionId: KycSourceOfFundsOptionId;
  label: string;
  isSelected: boolean;
  onSelect: (optionId: KycSourceOfFundsOptionId) => void;
};

const KycSourceOfFundsOption = memo(
  ({optionId, label, isSelected, onSelect}: KycSourceOfFundsOptionProps) => {
    const handleClick = useCallback(() => {
      onSelect(optionId);
    }, [onSelect, optionId]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(optionId);
        }
      },
      [onSelect, optionId],
    );

    return (
      <Box
        id={`kyc-source-of-funds-option-${optionId}`}
        sentry-label={`KycSourceOfFunds option ${optionId}`}
        className={`kyc-source-of-funds-option ${isSelected ? 'selected' : ''}`}
        onClick={handleClick}
        role="radio"
        aria-checked={isSelected}
        tabIndex={0}
        onKeyDown={handleKeyDown}>
        <Box className="kyc-source-of-funds-option-radio" />
        <Typography className="kyc-source-of-funds-option-label">
          {label}
        </Typography>
      </Box>
    );
  },
);

KycSourceOfFundsOption.displayName = 'KycSourceOfFundsOption';

const KycSourceOfFundsContent = () => {
  const {t} = useTranslation();
  const {
    selectedOption,
    isShowTermsAndConditionsModal,
    disableButton,
    handleSelectOption,
    handleCloseTermsAndConditions,
    handleOnClickContinue,
  } = useViewModelProvider();

  return (
    <>
      <Box className="kyc-source-of-funds-page-container">
        <Box className="kyc-source-of-funds-titles">
          <Typography
            id="kyc-source-of-funds-title"
            className="kyc-source-of-funds-title">
            {t('kyc.source_of_funds.title')}
          </Typography>
          <Typography className="kyc-source-of-funds-subtitle">
            {t('kyc.source_of_funds.sub_title')}
          </Typography>
        </Box>

        <Box
          className="kyc-source-of-funds-options"
          role="radiogroup"
          aria-labelledby="kyc-source-of-funds-title">
          {KYC_SOURCE_OF_FUNDS_OPTION_IDS.map(optionId => (
            <KycSourceOfFundsOption
              key={optionId}
              optionId={optionId}
              label={t(`kyc.source_of_funds.options.${optionId}`)}
              isSelected={selectedOption === optionId}
              onSelect={handleSelectOption}
            />
          ))}
        </Box>

        <ButtonContainer sticky>
          <AppButton
            variant={AppButtonVariant.onPrimary}
            id="kyc-source-of-funds-continue-button"
            sentry-label="KycSourceOfFunds continue button"
            onClick={handleOnClickContinue}
            disabled={disableButton}
            label={t('kyc.source_of_funds.button')}
          />
        </ButtonContainer>
      </Box>

      <TermsAndConditionsModal
        open={isShowTermsAndConditionsModal}
        onClose={handleCloseTermsAndConditions}
      />
    </>
  );
};

const KycSourceOfFundsPage = () => (
  <ViewModelProvider>
    <KycSourceOfFundsContent />
  </ViewModelProvider>
);

export default KycSourceOfFundsPage;
